import {createSlice, isAnyOf, PayloadAction} from "@reduxjs/toolkit";
import {ICar, ICarColor, ICarEvent, IMilestoneDef, IStage, DONE_PAGE, DONE_STAGE, EVENTS_PAGE} from "./types";
import {ICursor} from "../api";
import {carMutations, fetchBoard, fetchCarHistory, fetchDeleteCar, fetchEvents, fetchMoreDone, fetchShipCar} from "./actions";

export interface IPendingMove {
    carId: string;
    to: string;
}

export interface IBoardState {
    isLoading: boolean;
    loaded: boolean;
    stages: IStage[];
    milestones: IMilestoneDef[];
    colors: ICarColor[];
    /** Машины по id. Порядок в колонке задаёт sortColumn, а не стор. */
    cars: Record<string, ICar>;
    doneHasMore: boolean;
    doneLoading: boolean;
    /** Конец последней загруженной страницы «На отгрузку» — курсор «Показать ещё» (S15). */
    doneCursor: ICursor | null;
    search: string;
    selectedId: string | null;
    /** Панель открыта из «Обсуждений» или уведомления — лента к непрочитанному / вниз; с карточки — сверху (S12.2). */
    openAtChat: boolean;
    /** Карточка уже стоит в новой колонке, пока открыто окно переноса. */
    pendingMove: IPendingMove | null;
    /** `mine` — режим «Мои» (S18): только свои события. */
    journal: {open: boolean; mine: boolean; items: ICarEvent[]; hasMore: boolean; loading: boolean; loaded: boolean};
    /** История открытой машины, по времени ↑. */
    history: {carId: string; events: ICarEvent[]; loading: boolean} | null;
}

export const initialState: IBoardState = {
    isLoading: false,
    loaded: false,
    stages: [],
    milestones: [],
    colors: [],
    cars: {},
    doneHasMore: false,
    doneLoading: false,
    doneCursor: null,
    search: "",
    selectedId: null,
    openAtChat: false,
    pendingMove: null,
    journal: {open: false, mine: false, items: [], hasMore: false, loading: false, loaded: false},
    history: null,
};

const remove = (state: IBoardState, id: string) => {
    delete state.cars[id];
    if (state.selectedId === id) {
        state.selectedId = null;
        state.history = null;
    }
    if (state.pendingMove?.carId === id) state.pendingMove = null;
};

const upsert = (state: IBoardState, car: ICar) => {
    const prev = state.cars[car.id];
    // Устаревшая копия (ответ пришёл позже события) — не откатывать.
    if (prev && Date.parse(car.updated_at) < Date.parse(prev.updated_at)) return;
    // Уехавшей на доске не место (S17.2): сообщение в её чате, правка вехи в Стате
    // или её история не возвращают карточку в «На отгрузку».
    if (car.shipped_at) {
        if (prev) remove(state, car.id);
        return;
    }
    state.cars[car.id] = car;
};

/** Курсор по последней машине страницы (сервер отдаёт новые сверху). */
const pageCursor = (page: ICar[]): ICursor | null => {
    const last = page[page.length - 1];
    return last ? {before: last.stage_entered_at, before_id: last.id} : null;
};

export const BoardSlice = createSlice({
    name: "board",
    initialState,
    reducers: {
        /** `board:car` и ответы мутаций: вставить или заменить; уехавшую — убрать (S17.2). */
        carUpserted(state, action: PayloadAction<ICar>) {
            upsert(state, action.payload);
        },
        /** `board:car_removed`. */
        carRemoved(state, action: PayloadAction<{id: string}>) {
            remove(state, action.payload.id);
        },
        /** `board:event`: в открытый журнал и в историю открытой машины. */
        eventReceived(state, action: PayloadAction<ICarEvent>) {
            const e = action.payload;
            if (state.journal.loaded && !state.journal.items.some((x) => x.id === e.id)) {
                state.journal.items.unshift(e);
            }
            const h = state.history;
            if (h && e.car_id === h.carId && !h.events.some((x) => x.id === e.id)) {
                h.events.push(e);
            }
        },
        /** `board:colors`: список целиком. */
        colorsReplaced(state, action: PayloadAction<ICarColor[]>) {
            state.colors = action.payload;
        },
        setSearch(state, action: PayloadAction<string>) {
            state.search = action.payload;
        },
        selectCar(state, action: PayloadAction<string | null>) {
            state.selectedId = action.payload;
            state.openAtChat = false;
            if (!action.payload) state.history = null;
        },
        selectCarAtChat(state, action: PayloadAction<string>) {
            state.selectedId = action.payload;
            state.openAtChat = true;
        },
        setPendingMove(state, action: PayloadAction<IPendingMove | null>) {
            state.pendingMove = action.payload;
        },
        setJournalOpen(state, action: PayloadAction<boolean>) {
            state.journal.open = action.payload;
            if (!action.payload) state.journal = {...initialState.journal};
        },
        /** «Все / Мои» (S18): список заново — его грузит JournalDialog, раз `loaded` сброшен. */
        setJournalMine(state, action: PayloadAction<boolean>) {
            if (state.journal.mine === action.payload) return;
            state.journal = {...state.journal, mine: action.payload, items: [], hasMore: false, loaded: false};
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchBoard.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchBoard.fulfilled, (state, action) => {
            const {stages, milestones, colors, cars} = action.payload;
            state.stages = [...stages].sort((a, b) => a.order - b.order);
            state.milestones = milestones;
            state.colors = colors;
            // Слияние, а не замена: событие, пришедшее, пока шёл запрос, свежее снимка.
            const prev = state.cars;
            state.cars = {};
            cars.forEach((c) => {
                if (prev[c.id]) state.cars[c.id] = prev[c.id];
                upsert(state, c);
            });
            const donePage = cars.filter((c) => c.stage === DONE_STAGE);
            state.doneHasMore = donePage.length >= DONE_PAGE;
            state.doneCursor = pageCursor(donePage);
            if (state.pendingMove && !state.cars[state.pendingMove.carId]) state.pendingMove = null;
            state.isLoading = false;
            state.loaded = true;
            if (state.selectedId && !state.cars[state.selectedId]) state.selectedId = null;
        });
        builder.addCase(fetchBoard.rejected, (state) => {
            state.isLoading = false;
        });

        builder.addCase(fetchMoreDone.pending, (state) => {
            state.doneLoading = true;
        });
        builder.addCase(fetchMoreDone.fulfilled, (state, action) => {
            action.payload.forEach((c) => upsert(state, c));
            state.doneHasMore = action.payload.length >= DONE_PAGE;
            state.doneCursor = pageCursor(action.payload) ?? state.doneCursor;
            state.doneLoading = false;
        });
        builder.addCase(fetchMoreDone.rejected, (state) => {
            state.doneLoading = false;
        });

        builder.addCase(fetchEvents.pending, (state) => {
            state.journal.loading = true;
        });
        builder.addCase(fetchEvents.fulfilled, (state, action) => {
            const {items, append, mine} = action.payload;
            // Ответ на прежний режим «Все / Мои» (S18): не класть, список загрузится заново.
            if (mine !== state.journal.mine) {
                state.journal.loading = false;
                return;
            }
            const known = new Set(append ? state.journal.items.map((x) => x.id) : []);
            const fresh = items.filter((x) => !known.has(x.id));
            state.journal.items = append ? [...state.journal.items, ...fresh] : items;
            state.journal.hasMore = items.length >= EVENTS_PAGE;
            state.journal.loading = false;
            state.journal.loaded = true;
        });
        // Ошибка — `loaded`, иначе эффект JournalDialog повторял запрос без конца (S18.1). Повтор —
        // переоткрытием журнала или `fetchAfterReconnect`.
        builder.addCase(fetchEvents.rejected, (state) => {
            state.journal.loading = false;
            state.journal.loaded = true;
        });

        builder.addCase(fetchCarHistory.pending, (state, action) => {
            state.history = {carId: action.meta.arg, events: [], loading: true};
        });
        builder.addCase(fetchCarHistory.fulfilled, (state, action) => {
            const {events, ...car} = action.payload;
            upsert(state, car);
            if (state.history?.carId === car.id) {
                state.history = {carId: car.id, events, loading: false};
            }
        });
        builder.addCase(fetchCarHistory.rejected, (state) => {
            if (state.history) state.history.loading = false;
        });

        builder.addCase(fetchDeleteCar.fulfilled, (state, action) => {
            remove(state, action.payload.id);
        });
        builder.addCase(fetchShipCar.fulfilled, (state, action) => {
            remove(state, action.payload.id);
        });
        builder.addMatcher(isAnyOf(...carMutations.map((m) => m.fulfilled)), (state, action: PayloadAction<ICar>) => {
            upsert(state, action.payload);
        });
    },
});

export const {
    carUpserted,
    carRemoved,
    eventReceived,
    colorsReplaced,
    setSearch,
    selectCar,
    selectCarAtChat,
    setPendingMove,
    setJournalOpen,
    setJournalMine,
} = BoardSlice.actions;

export default BoardSlice.reducer;
